import { getOffset, smoothScrollTo, createElementFromHTML } from '../../utilities/js/helper';
import './job-list.scss';

/**
 * Manages the Job List component, handling filtering, pagination,
 * and dynamic content updates via AJAX.
 */
class JobList {
    constructor (element) {
        // Defaults
        this.defaults = {
            initAttr: 'data-joblist',
            routeAttr: 'data-joblist-route',
            filterValue: 'filter',
            dynamicContentValue: 'dynamic-content'
        };

        // Elements
        this.$root = element;
        this.$$filter = this.$root.querySelectorAll(`[${this.defaults.initAttr}="${this.defaults.filterValue}"] select`);
        this.$dynamicContent = this.$root.querySelector(`[${this.defaults.initAttr}="${this.defaults.dynamicContentValue}"]`);

        // Properties
        this.searchParams = {};
        this.baseRoute = this.$root.getAttribute(this.defaults.routeAttr);
        this.url = null;
    }

    /**
     * Initializes the component by setting up event listeners.
     */
    initialize () {
        this.setEvents();
    }

    /**
     * Updates the internal URL property based on the base route and current search parameters.
     * Filters out undefined, null, or empty string values from the query parameters.
     */
    updateUrl () {
        // Build the request URL from baseRoute and current searchParams
        if (!this.baseRoute) {
            this.url = null;
            return;
        }
        const params = new URLSearchParams();

        // Iterate over searchParams and add valid values to the URL parameters
        Object.entries(this.searchParams || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== null && `${value}` !== '') {
                params.set(key, value);
            }
        });

        const hasQuery = this.baseRoute.includes('?');
        const query = params.toString();

        // Construct the full URL, appending parameters with '&' or '?' depending on the base route
        this.url = query ? `${this.baseRoute}${hasQuery ? '&' : '?'}${query}` : this.baseRoute;
    }

    toggleLoader (show = true) {
        if (show) {
            this.$dynamicContent.insertAdjacentHTML('beforeend', window.OPTIONS.loader);
        }

        this.$dynamicContent.setAttribute('aria-busy', show);
    }

    /**
     * Fetches updated content from the server based on the current URL configuration.
     * Parses the response, updates the DOM, and dispatches a content loaded event.
     * @returns {Promise<void>}
     */
    async updateContent () {
        if (!this.baseRoute) return;
        this.updateUrl();
        this.toggleLoader();
        if (!this.url) return;

        try {
            // Perform an AJAX request to fetch the new HTML content
            const response = await fetch(this.url, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });

            if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
            }

            const result = await response.json();

            // Convert the returned HTML string into DOM nodes
            const $html = createElementFromHTML(result);
            this.$$filter = $html.querySelectorAll(`[${this.defaults.initAttr}="${this.defaults.filterValue}"] select`);

            // Create a custom event to notify other scripts that new content has been injected
            // This is crucial for re-initializing components within the new HTML
            const contentUpdateEvent = new CustomEvent('content.loaded', {
                view: window,
                bubbles: true,
                cancelable: false,
                detail: {
                    $context: $html
                }
            });

            window.dispatchEvent(contentUpdateEvent);

            this.initalizeFilter();

            const { top: anchorTop } = getOffset(this.$root);
            const currentScroll = window.scrollY || window.pageYOffset;

            if (currentScroll > anchorTop) smoothScrollTo(0, anchorTop - 200, window.OPTIONS.speed);

            // Replace the existing dynamic content with the newly fetched elements
            setTimeout(() => {
                this.$dynamicContent?.replaceChildren(...$html.children);
                this.toggleLoader(false);
            }, window.OPTIONS.speed);
        } catch (error) {
            console.error(error.message);
        }
    }

    /**
     * Register change listeners for all filter <select> elements.
     *
     * When a filter changes, this method resets the current page to 1, updates the
     * corresponding key in {@link JobList#searchParams}, and triggers a content refresh.
     *
     * @returns {void} Does not return a value.
     */
    initalizeFilter () {
        // Attach change listeners to all filter select elements to trigger content updates
        this.$$filter.forEach(($filter) => {
            // Each select represents a filter; on change, update query state and reload results.
            $filter.addEventListener('change', () => {
                this.searchParams['page'] = 1;
                this.searchParams[$filter.name] = $filter.value;
                this.updateContent();
            });
        });
    }

    /**
     * Sets up event listeners for pagination changes and filter interactions.
     * @returns {Promise<void>}
     */
    async setEvents () {
        // Listen for pagination changes triggered by other components
        this.$root.addEventListener('pagination.change', (e) => {
            const eventDetails = e.detail;
            this.searchParams['page'] = eventDetails.value;
            this.updateContent();
        });

        this.initalizeFilter();
    }

    /**
     * Register change listeners for all filter <select> elements.
     *
     * When a filter changes, this method resets the current page to 1, updates the
     * corresponding key in {@link JobList#searchParams}, and triggers a content refresh.
     *
     * @returns {void} Does not return a value.
     */
}

export { JobList };

/**
 * Global event listener to initialize JobList instances when new content is loaded into the DOM.
 * This ensures job lists inside AJAX-loaded content are properly activated.
 */
window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;
    if ($context) {
        const $$roots = $context.querySelectorAll('[data-joblist="root"]');
        $$roots.forEach($root => {
            const $rootAPI = new JobList($root);
            $rootAPI.initialize();
        });
    }
});
